<template>
  <div class="auth-content">
    <div
      v-if="complete"
      class="auth-done"
    >
      <div class="auth-tick">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
      </div>
      <span class="auth-eyebrow">Account created</span>
      <h1>You're in</h1>
      <p>We've created an account for <strong>{{ createdEmail }}</strong>.</p>
      <router-link
        :to="destination"
        class="auth-continue"
      >
        {{ hasNext ? 'Continue where you left off' : 'Start browsing music' }}
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M3 8h10M9 4l4 4-4 4" />
        </svg>
      </router-link>
    </div>

    <template v-else>
      <div>
        <h1>Create account</h1>
        <p class="auth-switch">
          Already have an account?
          <router-link :to="{ name: 'login', query: $route.query }">
            Log in
          </router-link>
        </p>
      </div>

      <form
        class="auth-form"
        novalidate
        @submit.prevent="submit"
      >
        <p
          v-for="(err, i) in generalErrors"
          :key="i"
          class="auth-message"
          role="alert"
        >
          {{ err }}
        </p>
        <div class="auth-field">
          <label for="email">Email</label>
          <input
            id="email"
            ref="email"
            v-model.trim="email"
            type="email"
            name="email"
            autocomplete="email"
            placeholder="you@example.com"
            class="auth-input"
            :class="{ 'has-error': emailErrors.length }"
            :aria-invalid="emailErrors.length ? 'true' : 'false'"
            aria-describedby="email-error"
            :disabled="busy"
            @input="clearFieldErrors('email')"
          >
          <p
            v-for="(err, i) in emailErrors"
            id="email-error"
            :key="i"
            class="auth-error"
          >
            {{ err|capitalize }}
          </p>
        </div>
        <div class="auth-field">
          <label for="password">Password</label>
          <auth-password-input
            ref="password"
            v-model="password"
            autocomplete="new-password"
            describedby="password-rules"
            :has-error="passwordErrors.length > 0"
            :disabled="busy"
            @input="clearFieldErrors('password')"
          />
          <p
            v-for="(err, i) in passwordErrors"
            :key="i"
            class="auth-error"
          >
            {{ err|capitalize }}
          </p>
          <ul
            id="password-rules"
            class="auth-rules"
          >
            <li
              v-for="rule in rules"
              :key="rule.key"
              :class="{ 'is-ok': rule.ok }"
            >
              {{ rule.text }}
            </li>
          </ul>
        </div>
        <label
          class="auth-check"
          for="marketing"
        >
          <input
            id="marketing"
            v-model="hasAgreedToMarketingEmail"
            type="checkbox"
          >
          <span>Email me new music, playlists and offers from Sonata. You can unsubscribe at any time.</span>
        </label>
        <button
          type="submit"
          class="auth-submit"
          :disabled="busy"
        >
          <span
            v-if="busy"
            class="auth-spin"
          />
          <span>Continue</span>
        </button>
      </form>

      <p class="auth-legal">
        By creating an account, you agree to Sonata's
        <router-link
          :to="{ name: 'user-terms' }"
          target="_blank"
        >Terms of Use</router-link>,
        and confirm you've read our
        <router-link
          :to="{ name: 'privacy-policy' }"
          target="_blank"
        >Privacy Policy</router-link>.
      </p>
    </template>
  </div>
</template>

<script>
import AuthPasswordInput from '@/components/AuthPasswordInput.vue'
import teamClient from '@/api/teams.js'
import { defaultDestination, passwordRules, safeNext } from '@/utils/authPages.js'
import { identify as segmentIdentify, logShowSignupForm, logSignup } from '@/utils/segment.js'
import { identify as logrocketIdentify } from '@/utils/logrocket.js'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'CreateAccount',

  components: {
    AuthPasswordInput
  },

  data () {
    return {
      email: '',
      password: '',
      hasAgreedToMarketingEmail: true,
      formErrors: {},
      busy: false,
      complete: false,
      createdEmail: ''
    }
  },

  metaInfo: {
    title: 'Create account'
  },

  computed: {
    rules: function () {
      return passwordRules(this.password)
    },

    emailErrors: function () {
      return (this.formErrors.user && this.formErrors.user.email) || []
    },

    passwordErrors: function () {
      return (this.formErrors.user && this.formErrors.user.password) || []
    },

    // Anything the API rejects that has no field on this page.
    generalErrors: function () {
      const errors = [...(this.formErrors.non_field_errors || [])]
      const user = this.formErrors.user || {}
      Object.keys(user)
        .filter(key => !['email', 'password'].includes(key))
        .forEach(key => errors.push(...[].concat(user[key])))
      Object.keys(this.formErrors)
        .filter(key => !['user', 'non_field_errors'].includes(key))
        .forEach(key => {
          const value = this.formErrors[key]
          if (Array.isArray(value)) {
            errors.push(...value)
          } else if (value && typeof value === 'object') {
            Object.values(value).forEach(v => errors.push(...[].concat(v)))
          }
        })
      return errors
    },

    hasNext: function () {
      return Boolean(safeNext(this.$route.query.next))
    },

    destination: function () {
      return safeNext(this.$route.query.next) || defaultDestination()
    }
  },

  mounted () {
    logShowSignupForm()
    this.$nextTick(() => {
      if (this.$refs.email) this.$refs.email.focus()
    })
  },

  methods: {
    validate () {
      const user = {}
      if (!this.email) {
        user.email = ['Enter your email address.']
      } else if (!EMAIL_PATTERN.test(this.email)) {
        user.email = ['Enter an email address like name@example.com.']
      }
      if (!this.password) {
        user.password = ['Enter a password.']
      } else if (!this.rules.every(rule => rule.ok)) {
        user.password = ['Your password needs to meet both requirements below.']
      }
      return Object.keys(user).length ? { user } : {}
    },

    signupData () {
      return {
        // UTM source, medium, etc.
        campaign: this.$cookies.get('__campaign') || null,
        // Agreement is implied by the small print under the button.
        agreed_to_terms: true,
        client_terms_agreed_at: new Date().toISOString(),
        has_agreed_to_marketing_email: this.hasAgreedToMarketingEmail,
        user: {
          email: this.email,
          first_name: '',
          last_name: '',
          password: this.password
        },
        // Teams need a name and we no longer ask for one; it can be changed
        // on the profile page.
        team: {
          name: this.email
        }
      }
    },

    submit () {
      this.formErrors = this.validate()
      if (Object.keys(this.formErrors).length) {
        this.focusFirstError()
        return
      }

      this.busy = true
      return teamClient.register(this.signupData())
        .then(
          () => {
            logSignup()
            return this.login()
          },
          (error) => this.handleRegisterError(error)
        )
    },

    handleRegisterError (error) {
      this.busy = false
      const status = error.response && error.response.status
      if (status === 400) {
        this.formErrors = error.response.data
        this.focusFirstError()
        return
      }
      if (status === 429) {
        this.formErrors = { non_field_errors: ['Too many requests. Please wait a few minutes and try again.'] }
        return
      }
      this.formErrors = { non_field_errors: ['Something went wrong creating your account. Please try again.'] }
      return Promise.reject(error.response || error)
    },

    login () {
      return this.$store.dispatch('auth/login', { email: this.email, password: this.password })
        .then(
          () => this.completeSignup(),
          (error) => {
            // The account now exists, so signing up again would fail.
            this.busy = false
            this.formErrors = { non_field_errors: ['Your account was created but we couldn\'t log you in. Please log in with your email and password.'] }
            return Promise.reject(error.response || error)
          }
        )
    },

    completeSignup () {
      // The user is logged in from here on. If loading their account fails,
      // still show that it was created rather than leave the button spinning.
      return this.$store.dispatch('common/initAuthenticated')
        .then(() => {
          const user = this.$store.getters['user/currentUser']
          const team = this.$store.getters['user/currentTeam']
          const userTraits = {
            email: user.email,
            name: (user.first_name + ' ' + user.last_name).trim(),
            team: team.name
          }
          segmentIdentify(user.pk, userTraits)
          logrocketIdentify(user.pk, userTraits)
        })
        .finally(() => {
          this.createdEmail = this.email
          // Clear sensitive data from the component.
          this.password = ''
          this.busy = false
          this.complete = true
        })
    },

    clearFieldErrors (field) {
      if (this.formErrors.user && this.formErrors.user[field]) {
        this.$delete(this.formErrors.user, field)
      }
    },

    focusFirstError () {
      this.$nextTick(() => {
        if (this.emailErrors.length) {
          this.$refs.email.focus()
        } else if (this.passwordErrors.length) {
          this.$refs.password.focus()
        }
      })
    }
  }
}
</script>
